import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  BarChart3, Users, Building2, Briefcase, Star, ClipboardList,
  LayoutDashboard, FileText, LogOut, Menu, X, ChevronDown,
  UserCheck, Settings, ScrollText, Activity, Bot, ShieldCheck, AlertTriangle
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import AppFooter from '@/components/AppFooter';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  roles: string[];
  children?: { label: string; href: string; roles?: string[] }[];
}

const empresaNavItems: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    roles: ['admin', 'gestor', 'lider', 'funcionario'],
  },
  {
    label: 'Cadastros',
    href: '/cadastros',
    icon: Settings,
    roles: ['admin', 'gestor'],
    children: [
      { label: 'Áreas', href: '/cadastros/areas', roles: ['admin'] },
      { label: 'Cargos', href: '/cadastros/cargos', roles: ['admin'] },
      { label: 'Competências', href: '/cadastros/competencias', roles: ['admin', 'gestor'] },
      { label: 'Funcionários', href: '/cadastros/funcionarios' },
      { label: 'Avaliadores', href: '/cadastros/avaliadores', roles: ['admin'] },
      { label: 'Pesos por Cargo', href: '/cadastros/pesos', roles: ['admin', 'gestor'] },
      { label: 'Critérios de Decisão', href: '/cadastros/criterios', roles: ['admin'] },
    ],
  },
  {
    label: 'Ciclos de Avaliação',
    href: '/ciclos',
    icon: ClipboardList,
    roles: ['admin'],
  },
  {
    label: 'Avaliações',
    href: '/avaliacoes',
    icon: Star,
    roles: ['admin', 'gestor', 'lider', 'funcionario'],
    children: [
      { label: 'Avaliar', href: '/avaliacoes' },
      { label: 'Painel de Controle', href: '/avaliacoes/painel', roles: ['admin'] },
      { label: 'Plano de Desenvolvimento', href: '/avaliacoes/plano', roles: ['admin', 'gestor', 'lider'] },
    ],
  },
  {
    label: 'Relatórios',
    href: '/relatorios',
    icon: FileText,
    roles: ['admin', 'gestor'],
  },
  {
    label: 'Administração',
    href: '/admin',
    icon: Users,
    roles: ['admin'],
    children: [
      { label: 'Usuários do Sistema', href: '/admin/usuarios' },
      { label: 'Logs do Sistema', href: '/admin/logs' },
      { label: 'Performance', href: '/admin/performance' },
      { label: 'Prompts de IA', href: '/admin/prompts' },
    ],
  },
  {
    label: 'Configurações',
    href: '/configuracoes',
    icon: Settings,
    roles: ['admin', 'gestor', 'lider', 'funcionario'],
  },
];

const superadminNavItems: NavItem[] = [
  {
    label: 'Dashboard Global',
    href: '/superadmin/dashboard',
    icon: LayoutDashboard,
    roles: ['superadmin'],
  },
  {
    label: 'Empresas',
    href: '/superadmin/empresas',
    icon: Building2,
    roles: ['superadmin'],
  },
  {
    label: 'Administradores',
    href: '/superadmin/admins',
    icon: Users,
    roles: ['superadmin'],
  },
  {
    label: 'SuperAdmins',
    href: '/superadmin/superadmins',
    icon: ShieldCheck,
    roles: ['superadmin'],
  },
  {
    label: 'Logs e Auditoria',
    href: '/superadmin/logs',
    icon: ScrollText,
    roles: ['superadmin'],
  },
  {
    label: 'Performance',
    href: '/superadmin/performance',
    icon: Activity,
    roles: ['superadmin'],
  },
];

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, role, signOut, isImpersonating, impersonatedEmpresaNome, exitEmpresa } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedItems, setExpandedItems] = useState<string[]>(['/cadastros']);

  // When superadmin is impersonating, show empresa nav; otherwise show based on role
  const isSuperadminOnEmpresa = role === 'superadmin' && isImpersonating;
  const navItems = isSuperadminOnEmpresa ? empresaNavItems : (role === 'superadmin' ? superadminNavItems : empresaNavItems);
  
  // For impersonating superadmin, show all empresa items (as if admin)
  const effectiveRole = isSuperadminOnEmpresa ? 'admin' : role;
  const visibleItems = navItems.filter(item => effectiveRole && item.roles.includes(effectiveRole));

  const toggleExpanded = (href: string) => {
    setExpandedItems(prev =>
      prev.includes(href) ? prev.filter(h => h !== href) : [...prev, href]
    );
  };

  const handleExitEmpresa = () => {
    exitEmpresa();
    navigate('/superadmin/dashboard');
  };

  const roleLabel = {
    superadmin: 'Super Admin',
    admin: 'Admin / RH',
    gestor: 'Gestor / Diretoria',
    lider: 'Líder / Avaliador',
    funcionario: 'Funcionário',
  }[role ?? ''] ?? '';

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          'flex flex-col bg-sidebar text-sidebar-foreground transition-all duration-300',
          sidebarOpen ? 'w-64' : 'w-16'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-sidebar-border">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-sidebar-primary shrink-0" />
              <span className="font-bold text-white">PerformanceRH</span>
            </div>
          )}
          {!sidebarOpen && <BarChart3 className="h-6 w-6 text-sidebar-primary mx-auto" />}
          <Button
            variant="ghost"
            size="icon"
            className="text-sidebar-foreground hover:bg-sidebar-accent ml-auto"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>

        {/* Impersonation Banner */}
        {isImpersonating && sidebarOpen && (
          <div className="px-3 py-2 bg-amber-500/20 border-b border-amber-500/30">
            <div className="flex items-center gap-2 text-xs text-amber-200">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate font-medium">Visualizando: {impersonatedEmpresaNome}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-1 h-7 text-xs text-amber-200 hover:bg-amber-500/30 hover:text-amber-100"
              onClick={handleExitEmpresa}
            >
              ← Voltar ao SuperAdmin
            </Button>
          </div>
        )}
        {isImpersonating && !sidebarOpen && (
          <div className="px-1 py-2 bg-amber-500/20 border-b border-amber-500/30 flex justify-center">
            <Button
              variant="ghost"
              size="icon"
              className="text-amber-200 hover:bg-amber-500/30 h-8 w-8"
              onClick={handleExitEmpresa}
              title="Voltar ao SuperAdmin"
            >
              <AlertTriangle className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href ||
              location.pathname.startsWith(item.href + '/');
            const isExpanded = expandedItems.includes(item.href);
            const hasChildren = item.children && item.children.length > 0;

            return (
              <div key={item.href}>
                {hasChildren ? (
                  <button
                    onClick={() => toggleExpanded(item.href)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
                    isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground font-medium'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    )}
                  >
                    <Icon className={cn('h-5 w-5 shrink-0', isActive ? 'text-white' : 'text-[hsl(215,20%,65%)]')} />
                    {sidebarOpen && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        <ChevronDown className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')} />
                      </>
                    )}
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
                      isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground font-medium'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    )}
                  >
                    <Icon className={cn('h-5 w-5 shrink-0', isActive ? 'text-white' : 'text-[hsl(215,20%,65%)]')} />
                    {sidebarOpen && <span>{item.label}</span>}
                  </Link>
                )}

                {/* Sub-items */}
                {hasChildren && isExpanded && sidebarOpen && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-sidebar-border pl-3">
                    {item.children!
                      .filter(child => !child.roles || (effectiveRole && child.roles.includes(effectiveRole)))
                      .map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className={cn(
                          'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                          location.pathname === child.href
                            ? 'bg-sidebar-primary text-sidebar-primary-foreground font-medium'
                            : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                        )}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User info + logout */}
        <div className="border-t border-sidebar-border p-3" style={{ backgroundColor: 'hsl(var(--sidebar-profile))' }}>
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar-primary text-sidebar-primary-foreground text-sm font-medium">
                {user?.email?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-muted-foreground truncate">{user?.email}</p>
                <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-sidebar-primary text-sidebar-primary-foreground mt-0.5">{roleLabel}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-sidebar-foreground hover:bg-sidebar-accent"
                onClick={signOut}
                title="Sair"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="w-full text-sidebar-foreground hover:bg-sidebar-accent"
              onClick={signOut}
              title="Sair"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {/* Top banner for impersonation */}
        {isImpersonating && (
          <div className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between text-sm font-medium">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span>Você está visualizando <strong>{impersonatedEmpresaNome}</strong> como SuperAdmin</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-amber-950 hover:bg-amber-600 hover:text-amber-950"
              onClick={handleExitEmpresa}
            >
              Voltar ao Painel SuperAdmin
            </Button>
          </div>
        )}
        <div className="flex min-h-full flex-col">
          <div className="flex-1">{children}</div>
          <AppFooter />
        </div>
      </main>
    </div>
  );
}
